/**
 * Bucketing and clock labels for the workspace run timeline chart.
 *
 * WHY BUCKETS RATHER THAN ONE MARK PER EVENT. The timeline is up to 200 entries whose timestamps
 * cluster hard - a CI workspace pushes six times inside a minute and then sits quiet for a week.
 * Plotted individually those six collapse into one unreadable smudge at the right-hand edge, and
 * the quiet week carries no ink at all. Bucketing to a fixed width gives every interval the same
 * x cost, so the shape of the chart is the shape of the activity, and each bar is a count a
 * reader can actually take a number off.
 *
 * WHY THE DOMAIN IS ZERO-FILLED. Same rule as PushCadenceChart: an empty bucket is still a
 * bucket, holding its own slice of the axis with no bar on it, rather than being dropped so the
 * busy windows close ranks. A chart that skips its quiet windows compresses a fortnight of
 * silence into nothing and reads as continuous activity.
 *
 * The window is the DATA's span, not a server window - this endpoint returns the newest N entries
 * with no `since`, so the only honest axis is first-entry to last-entry.
 */
import { formatDate, formatDateTime } from '@/lib/format'
import type { TimelineEntry } from '@/lib/apiTypes'

export interface TimelineBucket {
  /** Inclusive, ms. Aligned to a multiple of `step` so labels land on round clock values. */
  start: number
  /** Exclusive, ms. */
  end: number
  /** Newest first, in feed order. */
  entries: TimelineEntry[]
  runs: number
  blocks: number
  overrides: number
}

export interface BucketedTimeline {
  buckets: TimelineBucket[]
  /** Bucket width in ms. Every label in the chart is written against this resolution. */
  step: number
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
export const DAY = 24 * HOUR

/**
 * Candidate bucket widths, coarsest-last. Only round clock intervals, because a bucket boundary
 * is floored to a multiple of the step and an odd width (say 7 minutes) puts every tick on a
 * meaningless time. Nothing above 30 days: past that the ladder is abandoned for an even split.
 */
const STEPS = [MINUTE, 5 * MINUTE, 15 * MINUTE, HOUR, 3 * HOUR, 6 * HOUR, 12 * HOUR,
  DAY, 2 * DAY, 7 * DAY, 14 * DAY, 30 * DAY]

/** Enough points to show a shape, few enough that each stays a hoverable target. */
export const TARGET_BUCKETS = 24

/** The narrowest ladder step that fits `spanMs` in `target` buckets. */
export function pickStep(spanMs: number, target = TARGET_BUCKETS): number {
  for (const step of STEPS) if (spanMs / step <= target) return step
  // A workspace whose first and last entry are years apart. Split the span evenly instead: the
  // bucket count stays bounded, at the cost of boundaries that are not round clock values.
  return Math.ceil(spanMs / target)
}

/** Groups the feed into fixed-width buckets over its own span, oldest bucket first. */
export function bucketTimeline(entries: TimelineEntry[], target = TARGET_BUCKETS): BucketedTimeline {
  if (entries.length === 0) return { buckets: [], step: HOUR }

  let min = entries[0]!.at
  let max = min
  for (const e of entries) {
    if (e.at < min) min = e.at
    if (e.at > max) max = e.at
  }

  const step = pickStep(max - min, target)
  const start = Math.floor(min / step) * step
  const count = Math.floor((max - start) / step) + 1

  const buckets: TimelineBucket[] = Array.from({ length: count }, (_, i) => ({
    start: start + i * step,
    end: start + (i + 1) * step,
    entries: [],
    runs: 0,
    blocks: 0,
    overrides: 0,
  }))

  for (const e of entries) {
    const bucket = buckets[Math.min(count - 1, Math.floor((e.at - start) / step))]!
    bucket.entries.push(e)
    if (e.kind === 'run') bucket.runs++
    else if (e.verdict === 'block') bucket.blocks++
    else if (e.verdict === 'override') bucket.overrides++
  }

  return { buckets, step }
}

/** The bucket's own label: `start`, to the resolution the step justifies. */
export function bucketLabel(bucket: TimelineBucket, step: number): string {
  return step < DAY ? formatDateTime(bucket.start) : formatDate(bucket.start)
}

/**
 * The short axis form. Driven by the SPAN as well as the step, because the two disagree often:
 * a 12-hour step over eight days produced ticks reading "13:00, 13:00, 12:00, 12:00" - correct,
 * and useless, since nothing on the axis said which day any of them was.
 */
export function axisTick(ts: number, step: number, spanMs: number): string {
  const d = new Date(ts)
  switch (tickForm(step, spanMs)) {
    case 'clock': return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    case 'month': return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
    case 'datetime': return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    default: return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
  }
}

/** Which of the four tick spellings the axis is using. The chart needs this as well as the text:
 *  `datetime` is twice the width of the others and five of them collide on a phone. */
export type TickForm = 'clock' | 'datetime' | 'date' | 'month'

export function tickForm(step: number, spanMs: number): TickForm {
  if (spanMs <= DAY) return 'clock'
  if (spanMs > 365 * DAY) return 'month'
  return step < DAY ? 'datetime' : 'date'
}

/** "2 runs · 1 refusal", or null for an empty bucket. Shared by the tooltip and the aria label. */
export function bucketSummary(bucket: TimelineBucket): string | null {
  const parts: string[] = []
  if (bucket.runs > 0) parts.push(`${bucket.runs} ${bucket.runs === 1 ? 'run' : 'runs'}`)
  if (bucket.blocks > 0) parts.push(`${bucket.blocks} refused`)
  if (bucket.overrides > 0) parts.push(`${bucket.overrides} ${bucket.overrides === 1 ? 'override' : 'overrides'}`)
  return parts.length > 0 ? parts.join(' · ') : null
}

/** "15-minute", "6-hour", "2-day" - the x resolution, for a caption that never hardcodes it. */
export function stepLabel(step: number): string {
  if (step < HOUR) return `${Math.round(step / MINUTE)}-minute`
  if (step < DAY) return `${Math.round(step / HOUR)}-hour`
  return `${Math.round(step / DAY)}-day`
}
